/* eslint-disable prefer-destructuring */
import { useRouter } from 'next/router'
import React, { createContext, useContext } from 'react'

import { MenusFragment, PageConfigQuery, usePageConfigQuery } from '../../operations.wp/types'

export type Menu = NonNullable<NonNullable<MenusFragment['nodes']>[number]>
type SiteConfig = NonNullable<PageConfigQuery['siteConfig']>

type LogosConfig = NonNullable<SiteConfig['logos']>

type Cookie = {
  buttonLabel?: string
  message: string
  tickerActive: boolean
  title: string
}
type CallToAction = { label: string; url: string }
type MenusConfig = Partial<{
  bottom: Menu
  footer1: Menu
  footer2: Menu
  footer3: Menu
  footer4: Menu
  header1: Menu
  header2: Menu
  header3: Menu
  socials: Menu
}>

export type PageConfigContextType = {
  callsToAction: CallToAction[]
  cookie?: Cookie
  logos: LogosConfig
  menus: MenusConfig
}

export const PageConfigContext = createContext<PageConfigContextType | undefined>(undefined)

export const valuesFromAPI = (data?: PageConfigQuery, pathname?: string) => {
  const values: PageConfigContextType = { callsToAction: [], logos: {}, menus: {} }
  if (!data) return values

  if (!pathname?.startsWith('/tickets')) {
    if (data?.siteConfig?.headerCTAs?.firstCTALabel && data?.siteConfig?.headerCTAs?.firstCTAUrl) {
      values.callsToAction.push({
        label: data.siteConfig.headerCTAs.firstCTALabel,
        url: data.siteConfig.headerCTAs.firstCTAUrl,
      })
    }
    if (
      data?.siteConfig?.headerCTAs?.secondCTALabel &&
      data?.siteConfig?.headerCTAs?.secondCTAUrl
    ) {
      values.callsToAction.push({
        label: data.siteConfig.headerCTAs.secondCTALabel,
        url: data.siteConfig.headerCTAs.secondCTAUrl,
      })
    }
  }
  if (data?.siteConfig?.logos) {
    values.logos = data.siteConfig.logos
  }
  if (data?.menuHeader1?.nodes?.[0]) {
    values.menus.header1 = data.menuHeader1.nodes[0]
  }
  if (data?.menuHeader2?.nodes?.[0]) {
    values.menus.header2 = data.menuHeader2.nodes[0]
  }
  if (data?.menuHeader3?.nodes?.[0]) {
    values.menus.header3 = data.menuHeader3.nodes[0]
  }
  if (data?.menuSocials?.nodes?.[0]) {
    values.menus.socials = data.menuSocials.nodes[0]
  }
  if (data?.menuBottom?.nodes?.[0]) {
    values.menus.bottom = data.menuBottom.nodes[0]
  }
  if (data?.menuFooter1?.nodes?.[0]) {
    values.menus.footer1 = data.menuFooter1.nodes[0]
  }
  if (data?.menuFooter2?.nodes?.[0]) {
    values.menus.footer2 = data.menuFooter2.nodes[0]
  }
  if (data?.menuFooter3?.nodes?.[0]) {
    values.menus.footer3 = data.menuFooter3.nodes[0]
  }
  if (data?.menuFooter4?.nodes?.[0]) {
    values.menus.footer4 = data.menuFooter4.nodes[0]
  }
  return values
}

type PageConfigProviderProps = { children: React.ReactNode }

const PageConfigProvider = ({ children }: PageConfigProviderProps) => {
  const { data } = usePageConfigQuery({ context: { wordpress: true } })
  const { pathname } = useRouter()
  const value = valuesFromAPI(data, pathname)
  return <PageConfigContext.Provider value={value}>{children}</PageConfigContext.Provider>
}

export default PageConfigProvider

export const usePageConfig = () => {
  const pageConfig = useContext(PageConfigContext)
  if (pageConfig === undefined) {
    throw new Error('usePageConfig called with no PageConfigProvider')
  }
  return pageConfig
}
